import { useEffect, useCallback, useState, useRef } from 'react'
import { pick } from 'lodash'
import { clearChallengeCache } from '@/components/NotebookShared/notebookDataCache'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import { useAuth } from '@/hooks/useAuth'
import { useDesktopEffect } from '@/hooks/useDesktopEffect'
import {
    isSavingWorkAtom,
    lastSaveWorkDateAtom,
    workTimeAtom,
    currentSolutionAtom,
    currentQueryAtom,
    currentNotesAtom,
    challengeIdAtom,
    taskAtom,
    challengeAtom,
} from '@/store/saveWorkAtoms'
import {
    workspaceChallengeIdAtom,
    workspaceChallengeAtom,
    workspaceTaskAtom,
    workspaceChartsAtom,
    workspaceErrorAtom,
    workspaceIsVisibleAtom,
    workspaceDisclaimerDialogOpenAtom,
    workspaceSolutionReviewVisibleAtom,
    workspaceRecentTipsAtom,
    workspaceTableMetadataAtom,
} from '@/store/workspaceAtoms'
import { useAtom, useSetAtom, useStore } from 'jotai'
import { useWorkTimeReporting } from '@/hooks/useWorkTimeReporting'
import { useTar } from '@/modules/i18n'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { Workspace } from './Workspace'
import { WorkspaceError, challengeErrorsToNotify } from './WorkspaceError'
import { WorkspaceInitialization } from './WorkspaceInitialization'
import { verifyChartsSolutionConsistency } from './chartsInvariant'
import { getChallengeErrorCode } from '@/utils/errorUtils'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { useDebouncedWorkSaver } from '@/hooks/useDebouncedWorkSaver'
import { queryClient } from '@/modules/queryClient'
import { ongoingChallengesQueryKeys } from '@/hooks/useOngoingChallenges'
import { useNavigate } from '@tanstack/react-router'
import {
    Dialog,
    DialogContent,
    Button,
    MarkdownEditor,
} from '@duck/ui'
import { SolutionReview } from './SolutionReview'

interface WorkspaceAllParentProps {
    slug: string
}

// Parse the server's last-save timestamp into a Date for the autosave indicator.
// Timezone guard: the backend emits a UTC ISO string (Mongoose Date -> toISOString,
// always with a 'Z'), but if a value ever arrives without timezone info we treat it
// as UTC rather than letting `new Date` silently parse it as the client's local time.
// Falls back to "now" when absent (freshly started challenge) or unparseable.
const parseWorkSavedAt = (savedAt?: string | null): Date => {
    if (!savedAt) return new Date()
    const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/.test(savedAt)
    const parsed = new Date(hasTimezone ? savedAt : `${savedAt}Z`)
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

export const WorkspaceParent = ({
    slug,
}: WorkspaceAllParentProps) => {
    const { isAuthenticated } = useAuth()
    const [challengeId, setChallengeId] = useAtom(workspaceChallengeIdAtom)
    const [challenge, setChallenge] = useAtom(workspaceChallengeAtom)
    const [task, setTask] = useAtom(workspaceTaskAtom)
    const [charts, setCharts] = useAtom(workspaceChartsAtom)
    const [error, setError] = useAtom(workspaceErrorAtom)
    const [isVisible, setIsVisible] = useAtom(workspaceIsVisibleAtom)
    const [isDisclaimerDialogOpen, setIsDisclaimerDialogOpen] = useAtom(workspaceDisclaimerDialogOpenAtom)
    const [isSolutionReviewVisible, setIsSolutionReviewVisible] = useAtom(workspaceSolutionReviewVisibleAtom)
    const store = useStore()
    const navigate = useNavigate()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { getSetting, setSetting } = useLocalSettings()

    const setIsSaving = useSetAtom(isSavingWorkAtom)
    const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
    const [workTime, setWorkTime] = useAtom(workTimeAtom)
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const setCurrentQuery = useSetAtom(currentQueryAtom)
    const setCurrentNotes = useSetAtom(currentNotesAtom)
    const setRecentTips = useSetAtom(workspaceRecentTipsAtom)
    const setTableMetadata = useSetAtom(workspaceTableMetadataAtom)

    const { schedule: scheduleWorkSave, cancel: cancelPendingWorkSave } = useDebouncedWorkSaver(challengeId)
    const setChallengeIdAtom = useSetAtom(challengeIdAtom)
    const setTaskAtom = useSetAtom(taskAtom)
    const setChallengeAtom = useSetAtom(challengeAtom)
    const { t } = useTar()

    const isBooting = useMemo(() => {
        const storedSlug = sessionStorage.getItem('challenge_unbooted')
        return Boolean(storedSlug && storedSlug === slug)
    }, [slug])

    // Reset workspace state when slug changes so initial loading kicks in
    useEffect(() => {
      setChallengeId(null)
      setChallenge(null)
      setTask(null)
      setCharts(null)
      setError(null)
      setIsVisible(false)
      setIsSubmitting(false)
      setIsSolutionReviewVisible(false)
      setIsDisclaimerDialogOpen(false)
      setRecentTips([])
      setTableMetadata(null)
    }, [
      slug,
      setChallengeId,
      setChallenge,
      setTask,
      setCharts,
      setError,
      setIsVisible,
      setIsSolutionReviewVisible,
      setIsDisclaimerDialogOpen,
      setIsSubmitting,
      setRecentTips,
      setTableMetadata,
    ])

    useWorkTimeReporting(challenge?.isSandbox ? null : challengeId)

    useDebouncedEffect(() => {
        const fetchChallenge = async () => {
            try {
                setError(null)
                const [{ data: { challenge, task } }] = await Promise.all([
                    apis.challengeService.getChallengeBySlug(slug),
                    new Promise(res => setTimeout(res, isBooting ? 6500 : 400)),
                ])
                sessionStorage.removeItem('challenge_unbooted')
                if (challenge.status === ChallengeService.AvailableChallengeStatusEnum.Finished) {
                    navigate({ to: '/challenges/completed' })
                    return
                }
                setChallenge(challenge)
                setChallengeId(challenge.id)
                setChallengeIdAtom(challenge.id)
                setChallengeAtom(challenge)
                setWorkTime(challenge.workTime)
                setTask(task)
                setTaskAtom(task)
                setCharts(challenge.workState?.charts || {})
                const solution = challenge.workState?.solution || ''
                setCurrentSolution(solution)
                const query = challenge.workState?.lastQuery || task.defaultQuery || ''
                setCurrentQuery(query)
                setCurrentNotes(challenge.workState?.notes || '')
                // Seed the save indicator from the server's last-save timestamp so it
                // reflects the true persisted state on entry (defaults to "now" for a
                // freshly started challenge). The atom's module-load default is otherwise
                // stale by the time the user reaches the workspace.
                setLastSaveDate(parseWorkSavedAt(challenge.workState?.savedAt))
                if (isBooting) {
                    await queryClient.invalidateQueries({ queryKey: ongoingChallengesQueryKeys.all() })
                }
                apis.challengeService.getRecentTips().then(({ data }) => {
                    setRecentTips(data.tips ?? [])
                }).catch(() => {
                    // Silently ignore — tips are non-critical
                })
                apis.challengeService.getTableMetadata({ challengeId: challenge.id }).then(({ data }) => {
                    const byName: Record<string, ChallengeService.TableMetadataEntry> = {}
                    for (const entry of data.tables ?? []) {
                        byName[entry.name] = entry
                    }
                    setTableMetadata(byName)
                }).catch((err) => {
                    console.error('Failed to fetch table metadata:', err)
                    // Resolve to empty so consumers can distinguish loaded-empty from still-loading
                    setTableMetadata({})
                })
            } catch (err) {
                const errorCode = getChallengeErrorCode(err) as ChallengeService.ChallengeErrorCode
                if (challengeErrorsToNotify.includes(errorCode)) {
                    Sentry.captureException(err) // Extra report from client on criticals
                }
                setError(errorCode || 'unknownError')
            }
        }

        if (slug) {
            if (isAuthenticated) {
                fetchChallenge()
            } else {
                setError('loggedOut')
            }
        }
    }, [slug, isAuthenticated])

    useDesktopEffect(() => {
        // Prevent scrolling
        document.body.style.overflow = 'hidden'

        // Cleanup function to restore scrolling
        return () => {
            document.body.style.overflow = ''
        }
    }, [])

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    // Handler to close disclaimer dialog and mark as dismissed
    const handleCloseDisclaimer = useCallback(() => {
        setIsDisclaimerDialogOpen(false)
        if (challengeId) {
            const dismissedDisclaimers = getSetting('challengeDisclaimerDismissed') || {}
            setSetting('challengeDisclaimerDismissed', {
                ...dismissedDisclaimers,
                [challengeId]: true,
            })
        }
    }, [challengeId, getSetting, setSetting])

    // Fade in Workspace when all data is ready
    useEffect(() => {
        if (challenge && challengeId && task && charts && !error) {
            // Small delay to ensure smooth transition
            const timer = setTimeout(() => {
                setIsVisible(true)
            }, 50)
            return () => clearTimeout(timer)
        } else {
            setIsVisible(false)
        }
    }, [challenge, challengeId, task, charts, error])

    // Disclaimer dialog
    useEffect(() => {
        if (challenge?.challengeDisclaimer && challengeId) {
            const dismissedDisclaimers = getSetting('challengeDisclaimerDismissed') || {}
            const isDismissed = dismissedDisclaimers[challengeId] === true

            if (!isDismissed) {
                setIsDisclaimerDialogOpen(true)
            }
        }
    }, [challenge?.challengeDisclaimer, challengeId, getSetting])

    const isMountedRef = useRef(true)
    useEffect(() => {
        isMountedRef.current = true
        return () => { isMountedRef.current = false }
    }, [])

    const handleChartAdded = useCallback(
        (solution: string, chart: ChallengeService.Chart) => {
            if (!challengeId || !chart.id) return
            // Discard any pending debounced solution save — it carries a pre-chart
            // version of the HTML and would clobber this write if it flushed later.
            cancelPendingWorkSave()
            setCurrentSolution(solution)
            const currentCharts = store.get(workspaceChartsAtom)
            const newCharts = { ...currentCharts, [chart.id]: chart }
            setCharts(newCharts)
            verifyChartsSolutionConsistency(challengeId, solution, newCharts, 'handleChartAdded')
            setIsSaving(true)
            apis.challengeService
                .saveWork({
                    challengeId,
                    workState: {
                        solution,
                        charts: newCharts,
                    },
                })
                .then(() => {
                    if (!isMountedRef.current) return
                    setLastSaveDate(new Date())
                    toast.success(t('common.chartAddedSuccessfully'))
                })
                .catch((err) => {
                    console.error('Failed to save chart:', err)
                    if (!isMountedRef.current) return
                    toast.error(t('errors.failedToAddChart'))
                })
                .finally(() => {
                    if (!isMountedRef.current) return
                    setIsSaving(false)
                })
        },
        [challengeId, store, cancelPendingWorkSave]
    )

    const handleChartRemoved = useCallback(
        (solution: string, remainingChartIds: string[]) => {
            if (!challengeId) {
                return
            }
            // Discard any pending debounced solution save — it carries a pre-removal
            // version of the HTML (still has the deleted chart's div) and would
            // re-introduce the orphan div if it flushed later.
            cancelPendingWorkSave()
            setCurrentSolution(solution)
            const charts = pick(
                store.get(workspaceChartsAtom) ?? {},
                remainingChartIds
            ) as ChallengeService.WorkState['charts']
            setCharts(charts)
            verifyChartsSolutionConsistency(challengeId, solution, charts, 'handleChartRemoved')
            setIsSaving(true)
            apis.challengeService
                .saveWork({
                    challengeId,
                    workState: {
                        solution,
                        charts,
                    },
                })
                .then(() => {
                    setLastSaveDate(new Date())
                })
                .catch((err) => {
                    console.error('Failed to save chart:', err)
                    toast.error(t('errors.failedToRemoveChart'))
                })
                .finally(() => {
                    setIsSaving(false)
                })
        },
        [challengeId, cancelPendingWorkSave, store]
    )

    const handleConfirmSubmit = useCallback(async () => {
        if (!challengeId || isSubmitting) return
        setIsSubmitting(true)
        // Discard any pending debounced solution save so it can't fire mid-submit
        // and reintroduce a stale HTML.
        cancelPendingWorkSave()
        const solution = store.get(currentSolutionAtom)?.trim() || ''
        const charts = store.get(workspaceChartsAtom) || {}
        verifyChartsSolutionConsistency(challengeId, solution, charts, 'handleConfirmSubmit')
        try {
            await apis.challengeService.saveWork({
                challengeId,
                workState: {
                    solution,
                    charts,
                },
            })
            const response = await apis.challengeService.submitSolution({ challengeId })
            if (response.data.success) {
                await clearChallengeCache(challengeId)
                await queryClient.invalidateQueries({ queryKey: ongoingChallengesQueryKeys.all() })
                toast.success(t('challenges.solutionSubmittedSuccessfully'))
                setIsSolutionReviewVisible(false)
                navigate({ to: '/challenges/completed', search: { submitted: challengeId } })
            } else {
                toast.error(t('challenges.failedToSubmitSolution'))
            }
        } catch (err) {
            console.error('Failed to submit solution:', err)
            toast.error(t('challenges.failedToSubmitSolution'))
        } finally {
            setIsSubmitting(false)
        }
    }, [challengeId, store, isSubmitting, navigate, t, cancelPendingWorkSave])

    const handleKeepWorking = useCallback(() => {
        setIsSolutionReviewVisible(false)
    }, [setIsSolutionReviewVisible])

    const handleSolutionChange = useCallback(
        (solution: string) => {
            scheduleWorkSave({ solution })
        },
        [scheduleWorkSave],
    )

    if (error) {
        return <WorkspaceError error={error} />
    }

    if (
        !challenge || !challengeId ||
        !task ||
        !charts
    ) {
        return <WorkspaceInitialization isBooting={isBooting} />
    }

    return (
        <>
            <div className="flex flex-1 relative min-h-0" data-id={`workspace-${challengeId}`}>
                <div
                    className="absolute inset-0 flex flex-1 transition-opacity duration-300 ease-in-out"
                    style={{
                        opacity: isVisible && !isSolutionReviewVisible ? 1 : 0,
                        pointerEvents: isSolutionReviewVisible ? 'none' : 'auto',
                    }}
                >
                    <Workspace
                        challenge={challenge}
                        task={task}
                        charts={charts}
                        challengeId={challengeId}
                        workTime={workTime}
                        handleSolutionChange={handleSolutionChange}
                        handleChartAdded={handleChartAdded}
                        handleChartRemoved={handleChartRemoved}
                    />
                </div>
                <div
                    className="absolute inset-0 flex flex-col transition-opacity duration-300 ease-in-out"
                    style={{
                        opacity: isSolutionReviewVisible ? 1 : 0,
                        pointerEvents: isSolutionReviewVisible ? 'auto' : 'none',
                    }}
                >
                    <SolutionReview
                        charts={charts}
                        challenge={challenge}
                        onKeepWorking={handleKeepWorking}
                        onSubmit={handleConfirmSubmit}
                        isSubmitting={isSubmitting}
                    />
                </div>
            </div>

            {challenge?.challengeDisclaimer && (
                <Dialog
                    open={isDisclaimerDialogOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            handleCloseDisclaimer()
                        } else {
                            setIsDisclaimerDialogOpen(open)
                        }
                    }}
                >
                    <DialogContent width="lg" className="max-h-[90vh] flex flex-col">
                        <div className="flex-1 overflow-hidden min-h-[400px]">
                            <MarkdownEditor
                                defaultValue={challenge.challengeDisclaimer}
                                mode="view"
                                className="h-full"
                                noBorder
                            />
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button onClick={handleCloseDisclaimer}>
                                {t('common.close')}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}

